import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Briefcase, ArrowRight, Users, Search, Mail, X, Loader2, Send, BadgeCheck, CircleDollarSign, UserPlus, Hourglass, Radar, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ENGINES } from "@/content/engines";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const CLEARANCE_CLS = { 1: "text-zinc-300 border-white/20", 2: "text-nc-blue border-nc-blue/50 bg-nc-blue/5", 3: "text-nc-orange border-nc-orange/50 bg-nc-orange/5" };
const EASE = [0.16, 1, 0.3, 1];

function ContactModal({ contractor, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/directory/contact", { contractor_id: contractor.id, ...form });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        data-testid="contact-modal"
        className="w-full max-w-lg border border-nc-blue/40 bg-[#0a0a0a] p-6 flex flex-col gap-4"
        initial={{ scale: 0.94, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase">Direct contact // relay protected</p>
            <p className="font-sans font-bold text-lg text-zinc-50 mt-1">Message {contractor.name}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-100 transition-colors"><X className="w-5 h-5" /></button>
        </div>
        {sent ? (
          <div data-testid="contact-sent" className="border border-nc-green/40 bg-nc-green/5 p-6 text-center">
            <BadgeCheck className="w-8 h-8 text-nc-green mx-auto mb-3" />
            <p className="font-mono text-xs text-zinc-300 leading-relaxed">Message sent. The contractor gets your email address and will reply directly — usually within a day.</p>
          </div>
        ) : (
          <>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">Your message goes straight to their inbox with your reply address. Their email stays private. For escrow-protected work, post the job on the marketplace instead.</p>
            <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">Your name *</label>
                <input data-testid="contact-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={80} />
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">Your email *</label>
                <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} maxLength={160} />
              </div>
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">Message * — what do you need built?</label>
              <textarea data-testid="contact-message" rows={5} className={`${inputCls} resize-none`} value={form.message} onChange={(e) => set("message", e.target.value)} maxLength={2000} placeholder="Scope, engine, deadline, budget ballpark — the more detail the faster the reply." />
            </div>
            <button data-testid="contact-send" disabled={busy || form.name.trim().length < 2 || !form.email.includes("@") || form.message.trim().length < 20} onClick={send} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-bold uppercase tracking-[0.2em] text-xs px-6 py-3.5 transition-colors flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {busy ? "Sending…" : "Send Message"}
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function ContractorCard({ c, i, onContact }) {
  return (
    <motion.div
      data-testid={`contractor-card-${i + 1}`}
      className="relative border border-white/10 bg-black/40 p-5 flex flex-col gap-3.5 hover:border-nc-blue/50 hover:bg-[#0a0a0a] transition-all duration-300 group overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.5, delay: (i % 6) * 0.06, ease: EASE }}
    >
      <div className="absolute -top-16 -right-16 w-40 h-40 bg-nc-blue/10 blur-[70px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      <div className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-nc-blue to-nc-orange transition-all duration-500 pointer-events-none" />

      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-full bg-nc-blue/15 border border-nc-blue/40 flex items-center justify-center font-sans font-bold text-sm text-nc-blue overflow-hidden group-hover:border-nc-blue/70 transition-colors duration-300">
            {c.has_avatar ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${c.id}`} alt="" className="w-full h-full object-cover" />
            ) : (
              (c.name || "C")[0].toUpperCase()
            )}
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-nc-green rounded-full border-2 border-black" title="Verified" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="font-sans font-bold text-sm text-zinc-50 truncate group-hover:text-nc-blue transition-colors">{c.name}</p>
            <BadgeCheck className="w-3.5 h-3.5 text-nc-blue shrink-0" />
          </div>
          <p className="font-mono text-[10px] text-nc-orange truncate mt-0.5">{c.tagline || c.title || "Contractor"}</p>
          <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em] mt-0.5">Member since {c.member_since || "—"}</p>
        </div>
      </div>

      {(c.specialties || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {c.specialties.slice(0, 5).map((s) => (
            <span key={s} className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 px-2 py-1 group-hover:border-white/25 transition-colors">{s}</span>
          ))}
          {c.specialties.length > 5 && <span className="font-mono text-[8px] text-zinc-600 px-1 py-1">+{c.specialties.length - 5}</span>}
        </div>
      )}

      <div className="flex items-center justify-between mt-auto pt-1 flex-wrap gap-2">
        <span className={`font-mono text-[8px] tracking-[0.15em] uppercase border px-2 py-1 flex items-center gap-1 ${CLEARANCE_CLS[c.clearance?.level || 1] || CLEARANCE_CLS[1]}`}>
          <ShieldCheck className="w-3 h-3" /> Clearance {c.clearance?.level || 1}
        </span>
        <div className="flex items-center gap-3">
          {c.rate && <span className="font-mono text-[9px] text-nc-green flex items-center gap-1"><CircleDollarSign className="w-3 h-3" /> {c.rate}</span>}
          {c.engagements_completed > 0 && (
            <span className="font-mono text-[8px] text-zinc-400 uppercase flex items-center gap-1"><Briefcase className="w-3 h-3" /> {c.engagements_completed} delivered</span>
          )}
        </div>
      </div>

      <button
        data-testid={`contractor-contact-${i + 1}`}
        onClick={() => onContact(c)}
        className="w-full font-mono text-[9px] tracking-[0.2em] uppercase border border-nc-blue/50 text-nc-blue hover:bg-nc-blue hover:text-white px-3 py-2.5 transition-all duration-300 flex items-center justify-center gap-2 mt-1"
      >
        <Mail className="w-3.5 h-3.5" /> Contact Directly
      </button>
    </motion.div>
  );
}

export default function DirectoryPage() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [engine, setEngine] = useState("");
  const [contact, setContact] = useState(null);

  useEffect(() => {
    api.get("/directory/contractors").then((r) => setData(r.data)).catch(() => setData({ count: 0, contractors: [], applicants: [], applicant_count: 0 }));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.contractors.filter((c) => {
      if (engine && !(c.engines || []).includes(engine)) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = [c.name, c.tagline, ...(c.specialties || [])].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, query, engine]);

  const filteredApplicants = useMemo(() => {
    if (!data) return [];
    return (data.applicants || []).filter((a) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return [a.name, ...(a.disciplines || [])].join(" ").toLowerCase().includes(q);
    });
  }, [data, query]);

  const enginesUsed = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.contractors.flatMap((c) => c.engines || []))];
  }, [data]);

  const delivered = useMemo(() => (data ? data.contractors.reduce((n, c) => n + (c.engagements_completed || 0), 0) : 0), [data]);

  return (
    <div data-testid="directory-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="Contractor Network — Verified Game Developers for Hire"
        description="Browse Specpixel's network of identity-verified contractors: FiveM (GTA V) developers, UE5 artists, 3D modellers and animators. Contact them directly or post a job for escrow-protected pitches."
        path="/directory"
        keywords="hire game developers, FiveM developer for hire, UE5 contractor, game dev freelancers, verified contractors, escrow game development"
      />
      <SiteNav />

      {/* cinematic hero */}
      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-40 animate-grid-drift" />
        <div className="absolute -top-32 left-1/4 w-[600px] h-[340px] bg-nc-blue/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute -bottom-24 right-[10%] w-[420px] h-[260px] bg-nc-orange/8 blur-[110px] rounded-full pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-nc-blue/60 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
            <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-5 flex items-center gap-2">
              <Radar className="w-4 h-4" /> Contract Network // Public Registry
            </p>
            <h1 className="font-sans font-bold uppercase tracking-tight text-4xl sm:text-6xl lg:text-7xl text-zinc-50 leading-[0.95]">
              Meet the contractors
              <span className="block text-transparent" style={{ WebkitTextStroke: "1.5px rgba(46,107,255,0.55)" }}>behind the work.</span>
            </h1>
            <p className="font-mono text-sm text-zinc-400 mt-6 max-w-2xl leading-relaxed">
              Every verified contractor is identity-checked, cleared, and rated by real delivered work. They build for
              Specpixel — and they can build for you. Message them directly, or post a job and let the pitches come to you.
            </p>

            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <Link to="/jobs" data-testid="directory-post-job-cta" className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3.5 hover:bg-nc-blue/80 transition-colors duration-300 inline-flex items-center gap-2" style={{ boxShadow: "0 0 24px rgba(46,107,255,0.3)" }}>
                Post a Job <ArrowRight className="w-4 h-4" />
              </Link>
              {data && (
                <span data-testid="directory-count" className="font-mono text-[10px] tracking-[0.2em] uppercase text-nc-green border border-nc-green/40 bg-nc-green/5 px-3 py-1.5 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> {data.count} verified contractors
                </span>
              )}
              {data && data.applicant_count > 0 && (
                <span data-testid="directory-applicant-count" className="font-mono text-[10px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 bg-nc-orange/5 px-3 py-1.5 flex items-center gap-1.5">
                  <UserPlus className="w-3.5 h-3.5" /> {data.applicant_count} applicants in the talent registry
                </span>
              )}
              {delivered > 0 && (
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 border border-white/15 px-3 py-1.5 flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5" /> {delivered} engagements delivered
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </header>

      {/* filters */}
      <div className="border-b border-white/10 bg-[#070707]/90 backdrop-blur-md sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input data-testid="directory-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, specialty, skill…" className={`${inputCls} pl-9`} />
          </div>
          <select data-testid="directory-engine-filter" value={engine} onChange={(e) => setEngine(e.target.value)} className="bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue">
            <option value="">All engines</option>
            {enginesUsed.map((e) => (
              <option key={e} value={e}>{ENGINES[e]?.label || e}</option>
            ))}
          </select>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 w-full flex-1">
        {/* verified network */}
        <div className="flex items-center gap-3 mb-8">
          <BadgeCheck className="w-4 h-4 text-nc-blue" strokeWidth={1.75} />
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase">Verified Network // Ready to hire</p>
          <div className="h-px flex-1 bg-gradient-to-r from-nc-blue/40 to-transparent" />
        </div>

        {!data ? (
          <div className="border border-white/10 p-10 text-center font-mono text-xs text-zinc-600">Loading the network…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-white/10 border-dashed p-12 text-center">
            <p className="font-mono text-xs text-zinc-500">
              {data.count === 0 ? "Contractors appear here as they verify and list." : "No contractors match those filters."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c, i) => (
              <ContractorCard key={c.id} c={c} i={i} onContact={setContact} />
            ))}
          </div>
        )}

        {/* Talent Registry — applicants working toward verification */}
        {filteredApplicants.length > 0 && (
          <section data-testid="talent-registry" className="mt-20 relative">
            <div className="absolute -top-10 right-0 w-[300px] h-[200px] bg-nc-orange/6 blur-[100px] rounded-full pointer-events-none" />
            <div className="flex items-center gap-3 mb-2">
              <UserPlus className="w-4 h-4 text-nc-orange" strokeWidth={1.75} />
              <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Talent Registry // Verification Pipeline</p>
              <div className="h-px flex-1 bg-gradient-to-r from-nc-orange/40 to-transparent" />
            </div>
            <p className="font-mono text-xs text-zinc-500 leading-relaxed max-w-3xl mb-3">
              {data.applicant_count} specialists have applied to join the network and are moving through verification.
              Shown privacy-safe while they clear the pipeline — the verified contractors above are ready to hire today.
            </p>
            <div className="flex items-center gap-2 mb-8 font-mono text-[8px] tracking-[0.2em] uppercase text-zinc-600">
              <span className="border border-nc-orange/40 text-nc-orange px-2 py-1">01 Applied</span>
              <span className="text-zinc-700">——</span>
              <span className="border border-white/15 px-2 py-1">02 Identity verified</span>
              <span className="text-zinc-700">——</span>
              <span className="border border-nc-blue/40 text-nc-blue px-2 py-1">03 Cleared & listed</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {filteredApplicants.map((a, i) => (
                <motion.div
                  key={a.id || i}
                  data-testid={i < 10 ? `applicant-card-${i + 1}` : undefined}
                  className="border border-dashed border-white/15 bg-black/30 p-4 flex flex-col gap-2.5 hover:border-nc-orange/40 hover:bg-[#0a0a0a]/60 transition-colors duration-300 group"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: (i % 10) * 0.03, ease: EASE }}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-white/5 border border-white/15 flex items-center justify-center font-sans font-bold text-xs text-zinc-400 shrink-0 group-hover:border-nc-orange/40 group-hover:text-nc-orange transition-colors">
                      {(a.name || "A")[0].toUpperCase()}
                    </div>
                    <p className="font-sans font-bold text-xs text-zinc-200 truncate">{a.name}</p>
                  </div>
                  {(a.disciplines || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {a.disciplines.slice(0, 3).map((d) => (
                        <span key={d} className="font-mono text-[7px] uppercase border border-white/10 text-zinc-500 px-1.5 py-0.5">{d}</span>
                      ))}
                      {a.disciplines.length > 3 && <span className="font-mono text-[7px] text-zinc-600 px-1 py-0.5">+{a.disciplines.length - 3}</span>}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-auto pt-1">
                    <span className="font-mono text-[7px] tracking-[0.15em] uppercase text-zinc-500 border border-white/10 px-1.5 py-0.5 flex items-center gap-1">
                      <Hourglass className="w-2.5 h-2.5 animate-pulse" /> Verifying
                    </span>
                    {a.member_since && <span className="font-mono text-[7px] text-zinc-600 uppercase">{a.member_since}</span>}
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="font-mono text-[9px] text-zinc-600 mt-6 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-nc-orange" /> Want the verified badge? <Link to="/careers" className="text-nc-orange hover:text-zinc-100 underline underline-offset-4 transition-colors">Apply to the registry</Link> — approval lists you above with direct client contact.
            </p>
          </section>
        )}

        <div className="relative border border-nc-blue/30 bg-nc-blue/5 p-8 mt-16 flex flex-col md:flex-row gap-6 items-start overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-nc-blue/15 blur-[90px] rounded-full pointer-events-none" />
          <Briefcase className="w-6 h-6 text-nc-blue shrink-0" strokeWidth={1.75} />
          <div className="flex-1 relative">
            <p className="font-sans font-bold uppercase text-sm text-zinc-100">Hire the network — escrow protected</p>
            <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed max-w-3xl">
              Post your job and verified contractors pitch you with rates and plans. Award a pitch and funds lock into
              Stripe escrow automatically — released only when you approve the work. The same protection Specpixel uses
              for its own studio engagements.
            </p>
          </div>
          <Link to="/jobs" className="relative font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3.5 hover:bg-nc-blue/80 transition-colors duration-300 shrink-0">
            Post a Job
          </Link>
        </div>
      </main>

      <AnimatePresence>{contact && <ContactModal contractor={contact} onClose={() => setContact(null)} />}</AnimatePresence>
      <SiteFooter />
    </div>
  );
}
